import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Linkedin, Mail, X } from "lucide-react";

const STORAGE_KEY = "pfizer_thankyou_shown_v1";
const SUPPRESS_PATHS = ["/results"];

/**
 * One-time welcome overlay shown on first load across the Pfizer demo.
 * Silent on /results (the shared infographic — meant to be unblocked for reading).
 */
export function ThankYouPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (SUPPRESS_PATHS.includes(pathname)) return;
    let shown = false;
    try {
      shown = window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      // privacy mode — fall through and just show in-session
    }
    if (shown) return;
    // brief delay so the page paints first
    const t = window.setTimeout(() => setOpen(true), 450);
    return () => window.clearTimeout(t);
  }, [pathname]);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* noop */
    }
    setOpen(false);
  };

  if (SUPPRESS_PATHS.includes(pathname)) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="thanks"
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm"
            onClick={dismiss}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            role="dialog"
            aria-labelledby="thankyou-title"
            aria-describedby="thankyou-body"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="glass glow-border relative w-full max-w-md rounded-2xl p-7 text-left sm:p-8"
          >
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full p-1.5 text-white/50 transition hover:bg-white/5 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="inline-flex items-center gap-1.5 border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-white/60">
              Thank you
            </div>
            <h2
              id="thankyou-title"
              className="mt-4 text-2xl font-bold leading-tight text-white sm:text-[28px]"
            >
              Thank you for attending the session.
            </h2>
            <p id="thankyou-body" className="mt-3 text-sm leading-relaxed text-white/70">
              Enjoy your agent. If you have any questions or want to take this further, reach out
              anytime.
            </p>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <a
                href="mailto:hello@ruicosta.ai?subject=Pfizer%20workshop%20follow-up"
                onClick={dismiss}
                className="btn-primary flex-1 text-sm"
              >
                <Mail className="h-4 w-4" /> hello@ruicosta.ai
              </a>
              <a
                href="https://www.linkedin.com/in/ruicostapt"
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismiss}
                className="btn-ghost flex-1 text-sm"
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
            </div>

            <button
              type="button"
              onClick={dismiss}
              className="mt-5 text-[11px] uppercase tracking-[0.22em] text-white/40 transition hover:text-white/70"
            >
              Dismiss
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
